import { getUnauthorizedHandler } from '@/contexts/AuthContext'

const FRIDAY_TOKEN_KEY = 'friday_token'
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8787'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** The parsed JSON response body, when the API returned one — lets callers
     * read structured fields (e.g. `existing_id` on a 409 duplicate) beyond
     * the human-readable `message`. */
    public readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function getToken(): string | null {
  return sessionStorage.getItem(FRIDAY_TOKEN_KEY)
}

export function authToken(): string | null {
  return sessionStorage.getItem(FRIDAY_TOKEN_KEY)
}

export async function fetchApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers })

  if (response.status === 401) {
    const handler = getUnauthorizedHandler()
    if (handler) await handler()
    throw new ApiError(401, 'Unauthorized')
  }

  if (response.status === 403) {
    throw new ApiError(403, 'Forbidden')
  }

  if (!response.ok) {
    let message = `API error ${response.status}`
    let body: unknown
    try {
      body = await response.json()
      const parsed = body as { error?: string }
      if (parsed.error) message = parsed.error
    } catch {
      // ignore json parse error
    }
    throw new ApiError(response.status, message, body)
  }

  // 204 No Content
  if (response.status === 204) return undefined as unknown as T

  return response.json() as Promise<T>
}

// Convenience methods
export const api = {
  get: <T>(path: string) => fetchApi<T>(path),
  post: <T>(path: string, body: unknown) =>
    fetchApi<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    fetchApi<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    fetchApi<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => fetchApi<T>(path, { method: 'DELETE' }),
  // Authed binary fetch (e.g. a Xero invoice PDF) → Blob. The bearer token can't
  // ride a plain <a href>, so callers fetch here then open an object URL.
  getBlob: async (path: string): Promise<Blob> => {
    const token = getToken()
    const res = await fetch(`${API_BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new ApiError(res.status, `API error ${res.status}`)
    return res.blob()
  },
}

export { API_BASE }
